import { useEffect, useState } from 'react';
import type { Section } from '../model/pages';
import { normalizeFormFieldDesign, type FormFieldDesign, type FormFieldKey, type FormFieldWidth } from '../model/form-fields';
import { useEditorApi } from './editorApi';
import { Block, BTN, HINT, LABEL, RangeField, SelectField, TEXT_BTN } from './InspectorKit';

type On = (patch: Partial<Section>) => void;
type ContactField = { key: FormFieldKey; label: string };
type FieldInfo = { fields: ContactField[]; hasOptions: boolean };
const CONTACT_LABELS: Record<FormFieldKey, string> = {
  first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone', company: 'Company', title: 'Job title',
};
const WIDTHS: ReadonlyArray<[FormFieldWidth, string]> = [
  ['full', 'Full width'], ['half', 'Half width'], ['third', 'One third'], ['two-thirds', 'Two thirds'],
];
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** Only expose contact fields actually enabled on the linked form. Multiple
 * contact questions share presentation, while their answers remain separate. */
export function pageFormFieldInfo(questions: unknown): FieldInfo {
  const found = new Set<FormFieldKey>();
  let hasOptions = false;
  for (const raw of Array.isArray(questions) ? questions : []) {
    const q = record(raw);
    if (q.type === 'choice' || q.type === 'multi') hasOptions = true;
    if (q.type !== 'contact') continue;
    const fields = record(q.fields);
    found.add('first_name'); found.add('last_name'); found.add('email');
    if (fields.phone !== false) found.add('phone');
    if (fields.company) found.add('company');
    if (fields.title) found.add('title');
  }
  return { fields: (Object.keys(CONTACT_LABELS) as FormFieldKey[]).filter((key) => found.has(key)).map((key) => ({ key, label: CONTACT_LABELS[key] })), hasOptions };
}

export function formFieldPatch(section: Section, patch: Partial<FormFieldDesign>): Partial<Section> {
  return { form_fields: normalizeFormFieldDesign({ ...section.form_fields, ...patch }) };
}

function SizeControl({ label, value, min, max, initial, onChange }: {
  label: string; value?: number; min: number; max: number; initial: number; onChange: (value: number | undefined) => void;
}) {
  if (value === undefined) return (
    <div className="flex min-w-0 items-center justify-between gap-2">
      <div className="grid min-w-0 gap-1"><span className={LABEL}>{label}</span><span className={HINT}>Automatic</span></div>
      <button type="button" className={`${TEXT_BTN} !min-h-11`} aria-label={`Customize ${label.toLowerCase()}`} onClick={() => onChange(initial)}>Customize</button>
    </div>
  );
  return <div className="[&_input]:!min-h-11"><RangeField label={label} ariaLabel={label} value={value} min={min} max={max} step={1} onChange={onChange}
    display={<span>{value}px <button type="button" className={`${TEXT_BTN} !min-h-11`} aria-label={`Reset ${label.toLowerCase()}`} onClick={() => onChange(undefined)}>Auto</button></span>} /></div>;
}

function FieldsBody({ section, formId, onChange }: { section: Section; formId: number | null; onChange: On }) {
  const api = useEditorApi();
  const [retry, setRetry] = useState(0);
  const [loaded, setLoaded] = useState<{ id: number; info?: FieldInfo; error?: boolean } | null>(null);
  useEffect(() => {
    if (formId == null) return;
    let active = true;
    setLoaded(null);
    void api.getCrmForm(formId).then((form) => {
      if (active) setLoaded({ id: formId, info: pageFormFieldInfo(form.questions) });
    }).catch(() => { if (active) setLoaded({ id: formId, error: true }); });
    return () => { active = false; };
  }, [api, formId, retry]);
  const info = loaded?.id === formId ? loaded.info : undefined;
  const failed = loaded?.id === formId && loaded.error;
  const design = normalizeFormFieldDesign(section.form_fields);
  const patch = (next: Partial<FormFieldDesign>) => onChange(formFieldPatch(section, next));
  const columns = (key: 'columns' | 'options_columns', label: string) => (
    <SelectField label={label} className="[&>select]:!min-h-11" value={design?.[key] === undefined ? '' : String(design[key])}
      onChange={(value) => patch({ [key]: value ? Number(value) as 1 | 2 | 3 : undefined })}>
      <option value="">Automatic</option>
      {[1, 2, 3].map((n) => <option key={n} value={n}>{n} {n === 1 ? 'column' : 'columns'}</option>)}
    </SelectField>
  );
  return (
    <>
      <p className={HINT}>Applies on this page. Fields stack on phones.</p>
      {(info?.fields.length || !info) ? columns('columns', 'Field columns') : null}
      {info?.hasOptions && columns('options_columns', 'Answer choice columns')}
      <SizeControl label="Field spacing" value={design?.gap} min={8} max={32} initial={14} onChange={(gap) => patch({ gap })} />
      <SizeControl label="Form padding" value={design?.padding} min={12} max={48} initial={24} onChange={(padding) => patch({ padding })} />
      <SizeControl label="Input height" value={design?.input_height} min={44} max={64} initial={48} onChange={(input_height) => patch({ input_height })} />
      {formId == null ? <p className={HINT}>Choose a form to set individual field widths.</p>
        : failed ? <div role="status" className="grid min-w-0 gap-1"><p className={HINT}>Field list could not load.</p><button type="button" className={`${TEXT_BTN} justify-self-start !min-h-11`} onClick={() => setRetry((n) => n + 1)}>Retry field list</button></div>
        : !info ? <p role="status" className={HINT}>Loading fields…</p>
        : info.fields.length > 0 && <details className="min-w-0">
          <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium">Individual field widths</summary>
          <div className="grid min-w-0 gap-3 pt-2">
            {info.fields.map(({ key, label }) => <SelectField key={key} label={`${label} width`} className="[&>select]:!min-h-11" value={design?.widths?.[key] ?? ''}
              onChange={(value) => patch({ widths: { ...design?.widths, [key]: value || undefined } as FormFieldDesign['widths'] })}>
              <option value="">From field columns</option>
              {WIDTHS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </SelectField>)}
          </div>
        </details>}
      <SelectField label="Form button width" className="[&>select]:!min-h-11" value={design?.button_width ?? ''}
        onChange={(value) => patch({ button_width: value ? value as FormFieldDesign['button_width'] : undefined })}>
        <option value="">Automatic</option><option value="auto">Fit text</option><option value="full">Full width</option>
      </SelectField>
      {design?.button_width !== 'full' && <SelectField label="Form button alignment" className="[&>select]:!min-h-11" value={design?.button_align ?? ''}
        onChange={(value) => patch({ button_align: value ? value as FormFieldDesign['button_align'] : undefined })}>
        <option value="">Automatic</option><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
      </SelectField>}
      {design && <button type="button" className={`${BTN} !min-h-11 justify-self-start`} onClick={() => onChange({ form_fields: undefined })}>Reset field styles</button>}
    </>
  );
}

export function FormFieldControls(props: { section: Section; formId: number | null; onChange: On }) {
  return <Block name="Form fields" title="Fields"><FieldsBody {...props} /></Block>;
}
